import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/core/api/client';
import type { V2AdvisorReport } from '../api/v2AdvisorReportApi';
import type { V2AssessmentReport } from '../api/v2ReportApi';
import type { V2Response, V2TakeContent } from '../api/v2TakeApi';
import type { DraftFields } from './simulatorDraft';

/**
 * The public simulator's V2 content — the same questions the token flow
 * serves, with no client, advisor or saved answers. 404 while the backend kill
 * switch (`LONGEVITY_SIMULATOR_ENABLED`) is off.
 */
export async function getSimulatorContent(): Promise<V2TakeContent> {
  const response = await apiClient.get<V2TakeContent>('/longevity-simulator/content');
  return response.data;
}

/** The HTTP status of a failed call: the client interceptor rejects with `{ statusCode }`, not the axios error. */
export const statusOf = (error: unknown): unknown =>
  typeof error === 'object' && error !== null
    ? (error as { statusCode?: unknown }).statusCode
    : undefined;

/** 404: the backend kill switch (`LONGEVITY_SIMULATOR_ENABLED`) is off. */
export const isSimulatorOff = (error: unknown): boolean => statusOf(error) === 404;

export function useSimulatorContent() {
  return useQuery({
    queryKey: ['longevity-simulator', 'content'],
    queryFn: getSimulatorContent,
    retry: false,
    staleTime: Infinity,
  });
}

/**
 * Saves the FP's email as a sales lead: 204, no body. 400 = bad
 * email or failed reCAPTCHA, 429 = throttled, 404 = kill switch off.
 */
export async function postSimulatorLead(lead: {
  email: string;
  recaptchaToken: string;
}): Promise<void> {
  await apiClient.post('/longevity-simulator/lead', lead);
}

/** Both views of one simulated assessment, from a single POST. */
export type SimulatorReports = {
  clientReport: V2AssessmentReport;
  advisorReport: V2AdvisorReport;
};

type ReportRequest = {
  discovery: { questionId: string; optionKeys: string[] }[];
  answers: { needId: string; response: V2Response }[];
  subjectFirstName?: string;
};

export type ReportDraft = Pick<DraftFields, 'discovery' | 'answers' | 'subjectName'>;

/**
 * The draft as the endpoint takes it: an emptied selection is unanswered, so
 * it is left out, and so is a blank name — without one the request is the
 * same as before the name existed. The name reaches only the reports; the
 * lead (`postSimulatorLead`) never takes it.
 */
export const toReportRequest = ({
  discovery,
  answers,
  subjectName,
}: ReportDraft): ReportRequest => {
  const name = subjectName?.trim();
  return {
    discovery: Object.entries(discovery)
      .filter(([, optionKeys]) => optionKeys.length > 0)
      .map(([questionId, optionKeys]) => ({ questionId, optionKeys })),
    answers: Object.entries(answers).map(([needId, response]) => ({ needId, response })),
    ...(name ? { subjectFirstName: name } : {}),
  };
};

export async function postSimulatorReports(request: ReportRequest): Promise<SimulatorReports> {
  const response = await apiClient.post<SimulatorReports>('/longevity-simulator/report', request);
  return response.data;
}

/**
 * Every 400 from the report means the body no longer fits the content — an
 * unknown id, a missing or repeated answer, an over-selection. The body is
 * built only from the draft, so it is stale, never worth retrying.
 */
export const isStaleDraft = (error: unknown): boolean => statusOf(error) === 400;

/** Posted once per set of answers; switching tabs reads the same result. */
export function useSimulatorReports(draft: ReportDraft) {
  const request = toReportRequest(draft);
  return useQuery({
    queryKey: ['longevity-simulator', 'report', request],
    queryFn: () => postSimulatorReports(request),
    retry: false,
    staleTime: Infinity,
  });
}
